import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Location, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { ROUTE_DQF, ROUTE_DQF_CRIMINAL_CONVICTION } from 'src/app/shared/routes';
import { CANADA_PROVINCES, CERTN_COUNTRIES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { DriverQualificationService } from '../service/driver-qualification.service';

const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const INVALID_CERNT_US_PROVINCES = ['US-AS', 'US-GU', 'US-PR', 'US-VI'];
@Component({
    selector: 'app-dqf-birth-location',
    templateUrl: './dqf-birth-location.component.html',
    styleUrls: ['./dqf-birth-location.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule
    ]
})
export class DqfBirthLocationComponent implements OnInit {
  errorMessage: string;
  birthForm: UntypedFormGroup;
  countries = CERTN_COUNTRIES;
  filteredCountries = CERTN_COUNTRIES;
  stateProvinces: { value: string; name: string }[];
  requiredMessage = REQUIRED_FIELD_MESSAGE;
  enableProvinceButton = true;
  invalidCertnUSProvinces = INVALID_CERNT_US_PROVINCES;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly location: Location,
    private readonly dqfService: DriverQualificationService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    // Initialize dqfId if it's not already set
    this.initializeDqfId();

    this.birthForm = this.fb.group({
      birthCountry: new UntypedFormControl('', Validators.required),
      birthProvince: new UntypedFormControl({ value: '', disabled: true }, Validators.required)
    });
  }

  private initializeDqfId(): void {
    if (!this.dqfService.dqfId) {
      // Try from existing dqfData first
      if (this.dqfService.dqfData?.id) {
        this.dqfService.dqfId = this.dqfService.dqfData.id;
      } else {
        // Try from session storage
        const storedDqfId = sessionStorage.getItem('currentDqfId');
        if (storedDqfId) {
          this.dqfService.dqfId = storedDqfId;
        }
      }
    }

    // Always store the current dqfId for the next component
    if (this.dqfService.dqfId) {
      sessionStorage.setItem('currentDqfId', this.dqfService.dqfId);
    }
  }

  // ngOnDestroy(){
  //   sessionStorage.removeItem('orderPccOutsideDqf');
  //   sessionStorage.removeItem('pccReportData');
  // }
  onCountryChange(value) {
    // Reset the filtered countries and clear search input when a selection is made
    this.filteredCountries = CERTN_COUNTRIES;
    this.clearSearchInput();
    
    switch (value) {
      case 'US': {
        this.stateProvinces = USA_PROVINCES.filter(el => !this.invalidCertnUSProvinces.includes(el.value));
        this.enableProvince();
        break;
      }
      case 'CA': {
        this.stateProvinces = CANADA_PROVINCES;
        this.enableProvince();
        break;
      }
      default: {
        this.stateProvinces = [];
        this.enableProvinceButton = false;
        this.birthForm.get('birthProvince').reset();
        this.birthForm.get('birthProvince').disable();
        break;
      }
    }
  }

  private clearSearchInput(): void {
    // Find and clear the search input field
    const searchInput = document.querySelector('.dropdown-search input') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
  }

  onCertnCountrySearch(event: any): void {
    const searchText = event?.target?.value?.trim();
    this.filterCountries(searchText);
  }

  filterCountries(searchText: string): void {
    if (searchText && typeof searchText === 'string') {
      this.filteredCountries = CERTN_COUNTRIES.filter(country => 
        country.key.toLowerCase().includes(searchText.toLowerCase())
      );
    } else {
      this.filteredCountries = CERTN_COUNTRIES;
    }
  }

  getSelectedCountryName(): string {
    const selectedValue = this.birthForm.get('birthCountry')?.value;
    if (selectedValue) {
      const selectedCountry = CERTN_COUNTRIES.find(country => country.value === selectedValue);
      return selectedCountry ? selectedCountry.key : '';
    }
    return '';
  }

  enableProvince(): void {
    this.enableProvinceButton = true;
    this.birthForm.get('birthProvince').reset();
    this.birthForm.get('birthProvince').enable();
  }

  onSubmit(): void {
    if (!this.birthForm.valid) {
      this.birthForm.markAllAsTouched();
      return;
    }

    // Determine flow and proceed
    this.ensureDqfIdBeforeNavigation(() => {
      this.saveBirthLocationData();
      // Navigate to criminal convictions
      this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_CRIMINAL_CONVICTION}`]);
    });
  }

  private saveBirthLocationData(): void {
    const birthLocationData = {
      birthCountry: this.birthForm.value?.birthCountry,
      birthState:
        this.birthForm.value?.birthCountry === 'US'
          ? this.birthForm.value?.birthProvince?.slice(3)
          : this.birthForm.value?.birthProvince
    };

    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    
    if (!orderPccOutsideofDQF && this.dqfService.dqfId) {
      // DQF flow - save to service
      this.dqfService.driverBirthLocationData = {
        reportData: {
          birthLocation: birthLocationData,
          convictions: []
        }
      };
    } else {
      // Outside DQF flow - save to session storage
      const reportData = {
        ...birthLocationData,
        convictions: []
      };
      sessionStorage.setItem('pccReportData', JSON.stringify(reportData));
    }
  }

  private ensureDqfIdBeforeNavigation(callback: () => void): void {
    if (this.dqfService.dqfId) {
      // Store it and proceed with DQF flow
      sessionStorage.setItem('currentDqfId', this.dqfService.dqfId);
      callback();
      return;
    }

    // Try to get latest DQF if no dqfId is available
    this.dqfService.getDqfs().subscribe({
      next: (dqfs: any[]) => {
        if (dqfs && dqfs.length > 0) {
          // DQF flow - use existing DQF
          const latestDqf = dqfs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          this.dqfService.dqfId = latestDqf.id;
          sessionStorage.setItem('currentDqfId', this.dqfService.dqfId);
          callback();
        } else {
          // Outside DQF flow - proceed without DQF
          sessionStorage.setItem('orderPccOutsideDqf', 'true');
          callback();
        }
      },
      error: (error) => {
        // API error - assume outside DQF flow
        sessionStorage.setItem('orderPccOutsideDqf', 'true');
        callback();
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }
}
